const React = require('react');
const { FormProvider, useForm: useReactHookForm } = require('react-hook-form');

const useSyncedFormDefaults = require('./useSyncedFormDefaults');

const { useMemo } = React;
const {
    buildFieldProviderConfig,
    buildFormModel,
    buildFormProps,
    buildRenderForm,
    buildUseFormOptions
} = require('./formHookUtils');
const {
    areEquivalent,
    buildDefaults,
    normalizeFields,
    stringifyDefaults
} = require('./formModelUtils');
const {
    buildSubmitHandler,
    noop,
    wrapHandleSubmit
} = require('./formSubmitUtils');

module.exports = function usePrimitiveFormController(options = {}) {

    const {
        defaults,
        fields,
        formId,
        formOptions = {},
        onInvalidSubmit,
        onSubmit = noop,
        onSubmitError,
        resolver,
        schema,
        syncDefaults = true
    } = options;

    const memoFields = useMemo(() => normalizeFields(fields), [fields]);
    const processedDefaults = useMemo(() => buildDefaults({
        defaults,
        fields: memoFields
    }), [defaults, memoFields]);
    const processedDefaultsSignature = useMemo(
        () => stringifyDefaults(processedDefaults),
        [processedDefaults]
    );

    const methods = wrapHandleSubmit(useReactHookForm(buildUseFormOptions({
        formOptions,
        processedDefaults,
        resolver,
        schema
    })));
    const formModel = useMemo(() => buildFormModel({
        fields: memoFields,
        methods
    }), [memoFields, methods]);
    const formConfig = useMemo(() => buildFieldProviderConfig({
        fields: memoFields,
        formId
    }), [formId, memoFields]);
    const submitHandler = useMemo(() => buildSubmitHandler({
        fields: memoFields,
        formId,
        methods,
        onInvalidSubmit,
        onSubmit,
        onSubmitError
    }), [formId, memoFields, methods, onInvalidSubmit, onSubmit, onSubmitError]);
    const formProps = useMemo(() => buildFormProps({
        formId,
        submitHandler
    }), [formId, submitHandler]);
    const renderForm = useMemo(() => buildRenderForm({
        FormProvider,
        formProps,
        methods
    }), [formProps, methods]);

    useSyncedFormDefaults({
        areEquivalent,
        methods,
        processedDefaults,
        processedDefaultsSignature,
        syncDefaults
    });

    return {
        defaults: processedDefaults,
        fields: memoFields,
        formConfig,
        formModel,
        formProps,
        methods,
        renderForm,
        submitHandler
    };
};
